import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLeadModal } from "./LeadModal";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Como funciona", hash: "#como-funciona", testId: "nav-link-como-funciona" },
  { label: "Imobiliárias", hash: "#imobiliarias", testId: "nav-link-imobiliarias" },
  { label: "Serviços", hash: "#servicos", testId: "nav-link-servicos" },
  { label: "Tecnologia", hash: "#tecnologia", testId: "nav-link-tecnologia" },
  { label: "Parceiros", hash: "#parceiros", testId: "nav-link-parceiros" },
  { label: "FAQ", hash: "#faq", testId: "nav-link-faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openModal } = useLeadModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollToId(hash);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "border-b border-slate-200/70 bg-white/85 shadow-[0_8px_30px_rgba(11,25,44,0.06)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          onClick={() => go("#inicio")}
          aria-label="Ir para o início"
          data-testid="nav-logo-button"
          className="transition-transform duration-300 hover:scale-[1.03]"
        >
          <Logo />
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegação principal">
          {LINKS.map((link) => (
            <button
              key={link.hash}
              onClick={() => go(link.hash)}
              data-testid={link.testId}
              className="group relative text-sm font-medium text-slate-600 transition-colors duration-300 hover:text-petrol"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full bg-brand transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => openModal()}
            data-testid="nav-cta-button"
            className="group hidden items-center gap-2 rounded-full bg-petrol px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand sm:inline-flex"
          >
            Solicitar atendimento
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label="Abrir menu"
            data-testid="nav-mobile-menu-button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/70 text-petrol lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden border-b border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-5 py-4">
              {LINKS.map((link) => (
                <button
                  key={link.hash}
                  onClick={() => go(link.hash)}
                  data-testid={`${link.testId}-mobile`}
                  className="block w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  openModal();
                }}
                data-testid="nav-cta-button-mobile"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white"
              >
                Solicitar atendimento
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
